import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, IconButton, InputBase, Box, Typography, Divider,
  CircularProgress, Avatar, List, ListItemButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

/**
 * SEARCHSHEET — fullscreen mobile search dialog.
 *
 * Replaces the second row that the old AppBar carried below the
 * Toolbar. On mobile we now show a search ICON in the top bar; tap
 * it and this dialog slides up, autofocuses the input, and shows
 * results inline as you type.
 *
 * The dialog is `fullScreen` so we get edge-to-edge use of the
 * limited mobile viewport. Closes on backdrop tap, the explicit X
 * button, or `Escape`.
 *
 * Why a separate component instead of reusing SearchBar: SearchBar
 * was designed to live inline in a toolbar (compact, results in a
 * dropdown). The mobile experience needs results inline, no popover,
 * full-bleed. Different shape, share the query function only.
 */

interface Results {
  users: Array<{ id: string; username: string; bio?: string }>;
  feeds: Array<{ id: string; feed_tag: string }>;
  posts: Array<{ id: string; value: string; user_record_id: string }>;
}

const EMPTY: Results = { users: [], feeds: [], posts: [] };

async function search(query: string): Promise<Results> {
  if (!query.trim()) return EMPTY;
  const [u, f, p] = await Promise.all([
    supabase.from('users').select('id, username, bio').textSearch('search_vector', query, { type: 'websearch' }).limit(8),
    supabase.from('feeds').select('id, feed_tag').textSearch('search_vector', query, { type: 'websearch' }).limit(8),
    supabase.from('posts').select('id, value, user_record_id').textSearch('search_vector', query, { type: 'websearch' }).limit(8),
  ]);
  return {
    users: (u.data ?? []) as any[],
    feeds: (f.data ?? []) as any[],
    posts: (p.data ?? []) as any[],
  };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const SearchSheet: React.FC<Props> = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Results>(EMPTY);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(EMPTY);
      // Autofocus after the dialog mounts
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await search(query);
        setResults(r);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  const totalResults = results.users.length + results.feeds.length + results.posts.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
        },
      }}
    >
      {/* Search header — replaces the dialog title */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          zIndex: 1,
        }}
      >
        <IconButton onClick={onClose} edge="start" aria-label="close search">
          <CloseIcon />
        </IconButton>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.75,
            borderRadius: 2,
            bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <InputBase
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search users, posts, feeds…"
            fullWidth
            sx={{ fontSize: '0.95rem' }}
          />
          {loading && <CircularProgress size={16} />}
        </Box>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {!query.trim() && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2" sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1 }}>
              [ TYPE TO SEARCH ]
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              users · feeds · posts
            </Typography>
          </Box>
        )}

        {query.trim() && !loading && totalResults === 0 && (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
            <Typography variant="body2" sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1 }}>
              [ NO MATCHES ]
            </Typography>
          </Box>
        )}

        {results.users.length > 0 && (
          <Section title="Users">
            <List disablePadding>
              {results.users.map((u) => (
                <ListItemButton
                  key={u.id}
                  component={Link}
                  to={`/${u.username}`}
                  onClick={onClose}
                  sx={{ gap: 1.5, py: 1.25 }}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.85rem' }}>
                    {u.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      @{u.username}
                    </Typography>
                    {u.bio && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {u.bio}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              ))}
            </List>
          </Section>
        )}

        {results.feeds.length > 0 && (
          <Section title="Feeds">
            <List disablePadding>
              {results.feeds.map((f) => (
                <ListItemButton
                  key={f.id}
                  component={Link}
                  to={`/feeds/${f.feed_tag}`}
                  onClick={onClose}
                  sx={{ py: 1.25 }}
                >
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'primary.main' }}>
                    #{f.feed_tag}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Section>
        )}

        {results.posts.length > 0 && (
          <Section title="Posts">
            <List disablePadding>
              {results.posts.map((p) => {
                const text = (p.value ?? '')
                  .replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
                  .replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1');
                return (
                  <ListItemButton
                    key={p.id}
                    component={Link}
                    to={`/posts/${p.id}`}
                    onClick={onClose}
                    sx={{ py: 1.25, flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        width: '100%',
                      }}
                    >
                      {text}
                    </Typography>
                  </ListItemButton>
                );
              })}
            </List>
          </Section>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        px: 2,
        pt: 2,
        pb: 0.5,
        color: 'text.disabled',
        fontFamily: '"VT323", monospace',
        letterSpacing: 1.5,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </Typography>
    {children}
    <Divider />
  </Box>
);

export default SearchSheet;
